// Standard DOOH aspect ratios used across the application
export const ASPECT_RATIO_DELTA = 0.05;

import { KNOWN_ASPECT_RATIOS } from '@/shared/constants/aspectRatios';

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/**
 * Calculates the nearest known aspect ratio from a resolution string or object.
 * Uses fuzzy matching with ASPECT_RATIO_DELTA tolerance.
 * Falls back to GCD-reduced ratio (e.g. "2:11") instead of "Custom: X.XX".
 * Returns "Unknown" only when resolution data is missing or zero.
 */
export const calculateAspectRatio = (
  resolution: string | { width: number; height: number } | undefined
): string => {
  if (!resolution) return 'Unknown';

  let width: number, height: number;

  if (typeof resolution === 'string') {
    [width, height] = resolution.split('x').map(Number);
  } else {
    ({ width, height } = resolution);
  }

  if (!width || !height) return 'Unknown';

  const decimalRatio = width / height;
  const matchedRatio = KNOWN_ASPECT_RATIOS.find(
    r => Math.abs(decimalRatio - r.decimal) <= ASPECT_RATIO_DELTA
  );

  if (matchedRatio) return matchedRatio.name;

  const divisor = gcd(Math.round(width), Math.round(height));
  return `${Math.round(width) / divisor}:${Math.round(height) / divisor}`;
};

/**
 * Auto-detects the best matching aspect ratio name from width and height.
 * Falls back to "16:9" if no match is found.
 */
export const autoDetectAspectRatio = (
  width: number,
  height: number
): string => {
  if (!width || !height) return '16:9';
  const decimalRatio = width / height;
  const matchedRatio = KNOWN_ASPECT_RATIOS.find(
    r => Math.abs(decimalRatio - r.decimal) <= ASPECT_RATIO_DELTA
  );
  return matchedRatio?.name ?? '16:9';
};
